import { Head, Link, router } from "@inertiajs/react"
import {
  Archive,
  ArchiveRestore,
  Edit,
  Mail,
  Plus,
  Trash2,
  UserCog,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminUsersPath, newAdminUserPath } from "@/routes"
import type { AdminUser, BreadcrumbItem, RoleOption } from "@/types"

interface Props {
  users: AdminUser[]
  roles: RoleOption[]
  can: { create: boolean; archive: boolean; destroy: boolean }
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Users", href: adminUsersPath() },
]

export default function Index({ users, can }: Props) {
  const active = users.filter((user) => !user.archived)
  const archived = users.filter((user) => user.archived)

  const handleArchive = (user: AdminUser) => {
    if (
      confirm(
        `Archive ${user.email}? They'll be signed out and lose access, but their property assignments stay.`,
      )
    ) {
      router.patch(
        `/admin/users/${user.id}/archive`,
        {},
        { preserveScroll: true },
      )
    }
  }

  const handleRestore = (user: AdminUser) => {
    router.patch(
      `/admin/users/${user.id}/restore`,
      {},
      { preserveScroll: true },
    )
  }

  const handleDelete = (user: AdminUser) => {
    if (
      confirm(
        `Permanently delete ${user.email}? This also removes their property assignments.`,
      )
    ) {
      router.delete(`/admin/users/${user.id}`)
    }
  }

  const handleResend = (user: AdminUser) => {
    router.post(
      `/admin/users/${user.id}/resend_invitation`,
      {},
      { preserveScroll: true },
    )
  }

  const renderRow = (user: AdminUser) => (
    <TableRow
      key={user.id}
      className={user.archived ? "opacity-60" : undefined}
    >
      <TableCell>
        <Link
          href={`/admin/users/${user.id}`}
          className="font-medium hover:underline"
        >
          {user.name}
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {user.email}
      </TableCell>
      <TableCell>
        <Badge variant="secondary" className="capitalize">
          {user.role.name}
        </Badge>
      </TableCell>
      <TableCell>
        {user.verified ? (
          <Badge variant="outline">Verified</Badge>
        ) : (
          <Badge variant="destructive">Unverified</Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {user.created_at}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            title="Re-send password setup email"
            onClick={() => handleResend(user)}
          >
            <Mail className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Edit"
            render={<Link href={`/admin/users/${user.id}/edit`} />}
          >
            <Edit className="h-4 w-4" />
          </Button>
          {can.archive &&
            (user.archived ? (
              <Button
                size="sm"
                variant="ghost"
                title="Restore"
                onClick={() => handleRestore(user)}
              >
                <ArchiveRestore className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                title="Archive"
                onClick={() => handleArchive(user)}
              >
                <Archive className="h-4 w-4" />
              </Button>
            ))}
          {can.destroy && (
            <Button
              size="sm"
              variant="ghost"
              title="Delete"
              onClick={() => handleDelete(user)}
            >
              <Trash2 className="text-destructive h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Users" />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Users</h1>
            <p className="text-muted-foreground text-sm">
              What each person can do is decided by their role — see Roles.
            </p>
          </div>
          {can.create && (
            <Button render={<Link href={newAdminUserPath()} />}>
              <Plus className="mr-2 h-4 w-4" /> Add User
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Active ({active.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {active.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserCog />
                  </EmptyMedia>
                  <EmptyTitle>No active users</EmptyTitle>
                  <EmptyDescription>
                    Add a user and they&apos;ll get an email to set their own
                    password.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{active.map(renderRow)}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {archived.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Archived ({archived.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4 text-sm">
                Archived users can&apos;t sign in and hold no permissions. Their
                property assignments and brochure history are untouched.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{archived.map(renderRow)}</TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
